import { Store } from 'redux';
import { LOCAL_STORAGE_FILTER_KEY } from '../areas/category/constants';
import { PRODUCT_COLLECTION_LAYOUT } from '../areas/product-collection/constants';
import { SIDEBAR_LOCALSTORAGE_KEY } from '../areas/sidebar/constants';
import { CO_BRANDING_BANNER_LOCALSTORAGE_STATE_KEY } from '../components/co-branding-banner/constants';

type LocalStorageKey = string;
interface PersistedState {
  /** The key that will be used as an accessor in LocalStorage */
  localStorageKey: LocalStorageKey;
  /** A function that selects the state to be persisted from the Redux store */
  stateSelector: (state: any) => any;
}

/**
 * LocalStorage keys in this array will be checked if they have any content
 * on app instantiation, and a LOCAL_STORAGE_RECEIVE action will be triggered
 * in Redux with the content from LocalStorage. It's then up to individual
 * reducers to handle this data as they see fit.
 *
 * When the state is updated, LocalStorage will be updated at each key with
 * the data returned by the stateSelector function applied to the Redux state.
 */
const persistedStates: PersistedState[] = [
  {
    localStorageKey: LOCAL_STORAGE_FILTER_KEY,
    stateSelector: state => state.areas.category.filter.showHorizontalFilter,
  },
  {
    localStorageKey: PRODUCT_COLLECTION_LAYOUT,
    stateSelector: state => state.areas.productCollection.userSelectedLayout,
  },
  {
    localStorageKey: SIDEBAR_LOCALSTORAGE_KEY,
    stateSelector: state => {
      const { tab, lists } = state.areas.sidebar;

      return {
        tab,
        listSort:
          lists.sort && lists.direction
            ? `${lists.sort}:${lists.direction}`
            : lists.sort || undefined,
      };
    },
  },
  {
    localStorageKey: CO_BRANDING_BANNER_LOCALSTORAGE_STATE_KEY,
    stateSelector: ({ coBrandingBanner: { lastAcknowledged } }) => ({
      lastAcknowledged,
    }),
  },
];

function getLocalStorageItem(key: string) {
  try {
    const serializedState = localStorage.getItem(key);
    if (serializedState === null) {
      return undefined;
    }
    return JSON.parse(serializedState);
  } catch (err) {
    return undefined;
  }
}

function setLocalStorageItem(key: LocalStorageKey, state: any) {
  try {
    const serializedState = JSON.stringify(state);
    localStorage.setItem(key, serializedState);
  } catch {
    // Ignore any errors on write
  }
}

function deleteLocalStorageItem(key: LocalStorageKey) {
  try {
    localStorage.removeItem(key);
  } catch {
    // Ignore any errors on delete
  }
}

function getLocalStorageState() {
  return persistedStates.reduce(
    (localStorageState, { localStorageKey }) => ({
      ...localStorageState,
      [localStorageKey]: getLocalStorageItem(localStorageKey),
    }),
    {}
  );
}

export function loadLocalStorageState(store: Store) {
  const localStorageState = getLocalStorageState();

  store.dispatch({
    type: 'LOCAL_STORAGE_RECEIVE',
    localStorageState,
  });
}

export function saveStateToLocalStorage(store: Store) {
  persistedStates.forEach(({ localStorageKey, stateSelector }) => {
    const state = stateSelector(store.getState());
    if (state === undefined) {
      deleteLocalStorageItem(localStorageKey);
    } else {
      setLocalStorageItem(localStorageKey, state);
    }
  });
}
